import {
    Alert,
    Box,
    Button,
    Chip,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { ContextStore } from '../store/contextStore';
import { useDeveloperApi } from './api';
import {
    CodeBlock,
    describeError,
    DetailDrawer,
    DeveloperShell,
    ErrorAlert,
    Field,
    LoadMore,
    Loading,
    mono,
    TableScroll,
    Timestamp,
    ToneChip,
} from './components';
import { WEBHOOK_KINDS, WEBHOOK_STATUSES, webhookStatus } from './labels';
import { DEVELOPER_PATHS } from './routes';
import type { WebhookDelivery, WebhookDeliveryDetail, WebhookResponse, WebhookSubscription } from './types';
import { useCursorList } from './useCursorList';

const StatusChip = ({ status }: { status: string }) => {
    const label = webhookStatus(status);
    return <ToneChip label={label.label} tone={label.tone} hint={label.hint} sandbox={status === 'captured'} />;
};

const ResponseView = ({ response }: { response: WebhookResponse | null }) => (
    <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Response{response?.status_code ? ` ${response.status_code}` : ''}
            {response?.content_type ? <Typography component="span" variant="caption" color="text.secondary"> {response.content_type}</Typography> : null}
        </Typography>
        {!response || response.body == null ? (
            <Typography variant="body2" color="text.secondary">{response ? 'No body kept.' : 'No answer.'}</Typography>
        ) : (
            <>
                <CodeBlock text={response.body} testId="webhook-response-body" />
                {response.truncated && <Typography variant="caption" color="text.secondary">Cut at 4 KB.</Typography>}
            </>
        )}
    </Box>
);

/** One attempt, as the test-event and redeliver buttons answer it. */
const AttemptResult = ({ attempt }: { attempt: WebhookDelivery }) => (
    <Alert
        severity={attempt.status === 'delivered' ? 'success' : attempt.status === 'captured' ? 'info' : 'error'}
        sx={{ mt: 1 }}
        data-testid="webhook-attempt-result"
    >
        {webhookStatus(attempt.status).label}
        {attempt.response?.status_code ? `: the endpoint answered ${attempt.response.status_code}` : ''}
        {attempt.duration_ms != null ? ` in ${attempt.duration_ms} ms` : ''}
        {attempt.error ? ` (${attempt.error})` : ''}.
    </Alert>
);

const DeliveryDetail = ({ deliveryId, onSent }: { deliveryId: string; onSent: () => void }) => {
    const api = useDeveloperApi();
    const { enqueueSnackbar } = useSnackbar();
    const { hasPerm } = React.useContext(ContextStore) as any;
    const [detail, setDetail] = React.useState<WebhookDeliveryDetail | null>(null);
    const [error, setError] = React.useState<unknown>(null);
    const [sending, setSending] = React.useState(false);
    const [attempt, setAttempt] = React.useState<WebhookDelivery | null>(null);
    const [sendError, setSendError] = React.useState<unknown>(null);

    const load = React.useCallback(() => {
        setError(null);
        api.webhookDelivery(deliveryId).then(setDetail).catch(setError);
    }, [api, deliveryId]);
    React.useEffect(() => {
        setDetail(null);
        setAttempt(null);
        load();
    }, [load]);

    if (error) {
        return <ErrorAlert error={error} onRetry={load} />;
    }
    if (!detail) {
        return <Loading />;
    }
    const redeliver = async () => {
        setSending(true);
        setSendError(null);
        try {
            const result = await api.redeliver(detail.id);
            setAttempt(result);
            enqueueSnackbar('Sent again', { variant: 'success' });
            onSent();
        } catch (e) {
            setSendError(e);
        } finally {
            setSending(false);
        }
    };

    return (
        <Box data-testid="webhook-delivery-detail">
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <StatusChip status={detail.status} />
                <Chip size="small" variant="outlined" label={WEBHOOK_KINDS[detail.kind] || detail.kind} />
                <Box sx={{ flexGrow: 1 }} />
                {detail.redeliverable && hasPerm?.('developer', 'outbound_manage') && (
                    <Button variant="outlined" size="small" onClick={redeliver} disabled={sending}>Send again</Button>
                )}
            </Stack>
            {attempt && <AttemptResult attempt={attempt} />}
            {sendError ? <Alert severity="error" sx={{ mt: 1 }}>{describeError(sendError)}</Alert> : null}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mt: 1 }}>
                <Field label="Sent"><Timestamp value={detail.created_ts} /></Field>
                <Field label="Event">{detail.event?.code || '—'}</Field>
                <Field label="Notification">{detail.notification?.name || '—'}</Field>
                <Field label="Booking id">{detail.booking_id ?? '—'}</Field>
                <Field label="Endpoint"><span style={mono}>{detail.method} {detail.url}</span></Field>
                {detail.redelivery_of && <Field label="Sends again"><span style={mono}>{detail.redelivery_of}</span></Field>}
                {detail.error && <Field label="Error">{detail.error}</Field>}
            </Box>
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>Request headers</Typography>
            <CodeBlock text={Object.entries(detail.request.headers || {}).map(([k, v]) => `${k}: ${v}`).join('\n')} maxHeight={160} />
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>Payload</Typography>
            {detail.request.body == null
                ? <Typography variant="body2" color="text.secondary">No longer kept.</Typography>
                : <CodeBlock text={detail.request.body} testId="webhook-payload" />}
            <ResponseView response={detail.response} />
        </Box>
    );
};

const Subscriptions = ({ onSent }: { onSent: () => void }) => {
    const api = useDeveloperApi();
    const { hasPerm } = React.useContext(ContextStore) as any;
    const [subs, setSubs] = React.useState<WebhookSubscription[] | null>(null);
    const [error, setError] = React.useState<unknown>(null);
    const [testing, setTesting] = React.useState<number | null>(null);
    // A failed call is kept as {failure}: an attempt itself carries an `error` field.
    const [results, setResults] = React.useState<Record<number, { attempt?: WebhookDelivery; failure?: unknown }>>({});
    const canSend = !!hasPerm?.('developer', 'outbound_manage');

    const load = React.useCallback(() => {
        setError(null);
        api.webhooks().then((res) => setSubs(res.results || [])).catch(setError);
    }, [api]);
    React.useEffect(() => {
        load();
    }, [load]);

    const sendTest = async (sub: WebhookSubscription) => {
        setTesting(sub.id);
        try {
            const attempt = await api.testWebhook(sub.id);
            setResults((prev) => ({ ...prev, [sub.id]: { attempt } }));
            onSent();
            load();
        } catch (e) {
            setResults((prev) => ({ ...prev, [sub.id]: { failure: e } }));
        } finally {
            setTesting(null);
        }
    };

    if (error) {
        return <ErrorAlert error={error} onRetry={load} />;
    }
    if (!subs) {
        return <Loading />;
    }
    if (subs.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{ my: 1 }}>
                No webhooks yet: add an HTTP delivery method to a notification to call your endpoint.
            </Typography>
        );
    }
    return (
        <Stack spacing={1} data-testid="webhook-subscriptions">
            {subs.map((sub) => {
                const result = results[sub.id];
                return (
                    <Paper key={sub.id} variant="outlined" sx={{ p: 1.5 }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {sub.notification.name}
                                    {!sub.notification.active && <Chip size="small" label="Notification off" sx={{ ml: 1 }} />}
                                    {!sub.enabled && <Chip size="small" label="Disabled" sx={{ ml: 1 }} />}
                                </Typography>
                                <Typography sx={mono}>{sub.method} {sub.url}</Typography>
                                {sub.last_delivery && (
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                                        <Typography variant="caption" color="text.secondary">Last:</Typography>
                                        <StatusChip status={sub.last_delivery.status} />
                                        <Typography variant="caption" color="text.secondary"><Timestamp value={sub.last_delivery.created_ts} /></Typography>
                                    </Stack>
                                )}
                            </Box>
                            {canSend && (
                                <Button size="small" variant="outlined" onClick={() => sendTest(sub)} disabled={testing !== null}>
                                    {testing === sub.id ? 'Sending…' : 'Send test event'}
                                </Button>
                            )}
                        </Stack>
                        {result?.attempt && <AttemptResult attempt={result.attempt} />}
                        {result?.failure ? <Alert severity="error" sx={{ mt: 1 }}>{describeError(result.failure)}</Alert> : null}
                    </Paper>
                );
            })}
        </Stack>
    );
};

/** Webhook deliveries (SBX D9): the tenant's outgoing webhooks, every delivery with the endpoint's
 *  answer, "Send again" and "Send test event" (built once with the MCP tools, M15). Every call goes
 *  through the egress guard: an address that resolves to a private network is refused, never called. */
const WebhooksPage = () => {
    const api = useDeveloperApi();
    const history = useHistory();
    const { deliveryId } = useParams<{ deliveryId?: string }>();
    const [status, setStatus] = React.useState('');
    const [kind, setKind] = React.useState('');
    const [event, setEvent] = React.useState('');
    const [appliedEvent, setAppliedEvent] = React.useState('');

    const list = useCursorList<WebhookDelivery>(api.webhookDeliveries, { status, kind, event: appliedEvent });

    return (
        <DeveloperShell title="Webhooks">
            <Typography variant="subtitle1" sx={{ fontWeight: 500, mt: 1, mb: 1 }}>Your webhooks</Typography>
            <Subscriptions onSent={list.reload} />
            <Typography variant="subtitle1" sx={{ fontWeight: 500, mt: 3 }}>Deliveries</Typography>
            <Box
                component="form"
                onSubmit={(ev: React.FormEvent) => { ev.preventDefault(); setAppliedEvent(event.trim()); }}
                sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, my: 1.5 }}
            >
                <TextField select size="small" label="Status" value={status} onChange={(ev) => setStatus(ev.target.value)} sx={{ minWidth: 170 }}>
                    <MenuItem value="">Any</MenuItem>
                    {Object.entries(WEBHOOK_STATUSES).map(([value, { label }]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Kind" value={kind} onChange={(ev) => setKind(ev.target.value)} sx={{ minWidth: 150 }}>
                    <MenuItem value="">Any</MenuItem>
                    {Object.entries(WEBHOOK_KINDS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                </TextField>
                <TextField size="small" label="Event" placeholder="booking.created" value={event} onChange={(ev) => setEvent(ev.target.value)} />
                <Button type="submit" variant="outlined">Search</Button>
            </Box>
            {list.error ? <ErrorAlert error={list.error} onRetry={list.reload} /> : null}
            {list.loading && list.items.length === 0 ? <Loading /> : (
                <TableScroll>
                    <Table size="small" data-testid="webhook-deliveries">
                        <TableHead>
                            <TableRow>
                                <TableCell>Sent</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Kind</TableCell>
                                <TableCell>Event</TableCell>
                                <TableCell>Endpoint</TableCell>
                                <TableCell>Answer</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {list.items.map((row) => (
                                <TableRow
                                    key={row.id}
                                    hover
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => history.push(`${DEVELOPER_PATHS.webhooks}/${encodeURIComponent(row.id)}`)}
                                    data-testid={`webhook-delivery-row-${row.id}`}
                                >
                                    <TableCell><Timestamp value={row.created_ts} /></TableCell>
                                    <TableCell><StatusChip status={row.status} /></TableCell>
                                    <TableCell>{WEBHOOK_KINDS[row.kind] || row.kind}</TableCell>
                                    <TableCell>{row.event?.code || '—'}</TableCell>
                                    <TableCell sx={{ maxWidth: 320 }}><Typography sx={mono}>{row.url || '—'}</Typography></TableCell>
                                    <TableCell>
                                        {row.response?.status_code ?? '—'}
                                        {row.duration_ms != null ? ` · ${row.duration_ms} ms` : ''}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {!list.loading && list.items.length === 0 && !list.error && (
                                <TableRow><TableCell colSpan={6}><Typography variant="body2" color="text.secondary">No deliveries match.</Typography></TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableScroll>
            )}
            <LoadMore hasMore={list.hasMore} loading={list.loading} onClick={list.loadMore} />
            <DetailDrawer open={!!deliveryId} onClose={() => history.push(DEVELOPER_PATHS.webhooks)} title={<Typography variant="h6">Webhook delivery</Typography>}>
                {deliveryId && <DeliveryDetail deliveryId={deliveryId} onSent={list.reload} />}
            </DetailDrawer>
        </DeveloperShell>
    );
};

export default WebhooksPage;
